import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Input,
  Button,
  Typography,
  Card,
  CardBody,
  Spinner,
  Alert
} from "@material-tailwind/react";
import { userAjaxController } from "../Common/Ajax/UserAjaxController";

export const PasswordResetPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("t") ?? "";

  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const resp = await userAjaxController.verifyResetPasswordToken(token);

        if (resp.isValid) {
          setTokenValid(true);
        } else {
          setErrorMessage(
            resp.errors[0]?.errorMessage ??
            "This password reset link is invalid or has expired."
          );
        }
      } catch {
        setErrorMessage(
          "Could not verify the reset link. Please request a new password reset email."
        );
      }

      setLoading(false);
    };

    verifyToken();
  }, [token]);

  const submitNewPassword = async () => {
    setErrorMessage("");

    if (!password.trim() || !confirmPassword.trim()) {
      setErrorMessage("Please fill in both password fields.");
      return;
    }

    try {
      const resp = await userAjaxController.resetPassword(
        token,
        password,
        confirmPassword
      );

      if (resp.isValid) {
        setResetSuccess(true);

        setTimeout(() => {
          navigate("/");
        }, 5000);
      } else {
        setErrorMessage(
          resp.errors[0]?.errorMessage ??
          "Password reset failed. Please try again or request a new link."
        );
      }
    } catch {
      setErrorMessage("Unexpected error occurred. Please try again later.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <Spinner className="h-12 w-12" />
      </div>
    );
  }

  if (!tokenValid && !resetSuccess) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <Card className="p-8 w-full max-w-md shadow-xl">
          <Typography variant="h4" color="red" className="mb-3 text-center">
            Invalid Link
          </Typography>
          <Typography className="text-center text-gray-600">
            {errorMessage}
          </Typography>
          <Button
            className="mt-4 bg-purple text-white w-full hover:bg-purple/80"
            color="purple"
            onClick={() => navigate("/")}
          >
            Back to Home
          </Button>
        </Card>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <Card className="p-8 w-full max-w-md shadow-xl text-center">
          <Typography variant="h4" color="green" className="mb-3 text-center">
            <span className="font-semibold">Password updated successfully!</span>
          </Typography>
          <Typography className="text-center text-gray-700">
            You'll be redirected to the homepage shortly.
          </Typography>
          <Spinner className="h-12 w-12 mt-6 mx-auto" />
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center h-[80vh]">
      <Card className="p-8 w-full max-w-md shadow-xl">
        <Typography variant="h4" className="mb-4 text-center">
          Set a New Password
        </Typography>

        <CardBody className="flex flex-col gap-6">
          <Input
            label="New Password"
            type="password"
            size="lg"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Input
            label="Confirm Password"
            type="password"
            size="lg"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          {errorMessage && (
            <Alert color="red" className="text-sm">
              {errorMessage}
            </Alert>
          )}

          <Button
            size="lg"
            className="bg-purple text-white w-full"
            onClick={submitNewPassword}
          >
            Update Password
          </Button>
        </CardBody>
      </Card>
    </div>
  );
};
